import React, { ReactElement } from 'react';
import type { Route } from 'next';
import Link from 'next/link';
import Image from 'next/image';
import classNames from 'classnames/bind';
import Breadcrumbs from 'components/Breadcrumbs';
import useWindowSize from 'utils/hooks/useWindowSize';
import styles from 'sections/DigitalTools/ShipSchedules/components/SectionHeader/SectionHeader.module.scss';

const cn = classNames.bind(styles);

const SectionHeader = (): ReactElement => {
  const { width: screenWidth } = useWindowSize();

  return (
    <section className={cn('header')}>
      <div className={cn('container')}>
        <Breadcrumbs
          mTop={64}
          mode="light"
          links={[
            { title: 'Digital Tools', href: '/digital-tools' as Route },
            { title: 'Ship Schedules', href: '' as Route },
          ]}
        />

        <Image
          className={cn('header__bg-ellipse')}
          src="/images/pages/DigitalTools/ShipSchedules/bg-ellipse-01.svg"
          alt="ellipse"
          width={300}
          height={100}
        />

        <div className={cn('header__info')}>
          <h1 className={cn('header__heading')}>
            {screenWidth < 1025 &&
              'Vessel Schedule Tracking Tool for Freight Forwarders'
            }
            {screenWidth >= 1025 &&
              <>
                <span>Vessel Schedule</span>{' '}
                <span>Tracking Tool for</span>{' '}
                <span>Freight Forwarders</span>
              </>
            }
          </h1>
          <p className={cn('header__description')}>
            Simplify shipping strategies with the Shipping Schedules tool,
            a trusted solution by DP World and powered by SeaRates.
            Tailored for shippers and freight specialists,
            this smart scheduling system lets you search, select,
            and stay updated on global vessel timetables in real time,
            from streamlining supply chains to <Link href="/digital-tools/tracking-system">tracking</Link> transit times.
          </p>
          <div className={cn('header__buttons')}>
            <Link
              className={cn('header__button-check')}
              href='https://www.searates.com/integrations/ship-schedules/'
            >
              Check Vessel Schedules Now
            </Link>
          </div>
        </div>

        <div className={cn('header__image-box')}>
          <picture>
            <source
              srcSet="
                /images/pages/DigitalTools/ShipSchedules/schedules-mobile.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/schedules-mobile@2x.webp 2x
              "
              sizes="100vw"
              media="(max-width: 575px)"
            />
            <source
              srcSet="
                /images/pages/DigitalTools/ShipSchedules/schedules-tablet.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/schedules-tablet@2x.webp 2x
              "
              sizes="100vw"
              media="(max-width: 1024px)"
            />
            <source
              srcSet="
                /images/pages/DigitalTools/ShipSchedules/schedules-desktop.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/schedules-desktop@2x.webp 2x
              "
              sizes="100vw"
            />
            <img
              className={cn('header__image')}
              src="/images/pages/DigitalTools/ShipSchedules/schedules-desktop.webp"
              alt="ship schedules"
            />
          </picture>
        </div>

      </div>
    </section>
  );
};

export default SectionHeader;